import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';

import { Label, Input, SubmitButton, Warning } from '$partials/forms';
import { useLocale } from '$contexts/locale';
import Util from '$utilities/index';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */
const Newsletter = ({ title, body }) => {
    const [errorMessage, setErrorMessage] = useState();
    const [message, setMessage] = useState();
    const { translate: T } = useLocale();
    const { register, handleSubmit, reset, formState: { errors, isDirty } } = useForm();

    const hubspotFormId = '97f7b2e9-4b9f-4f80-93d9-d14879030ba3';
    const onSubmit = async (data) => {

        setErrorMessage(null);
        setMessage(null);

        if (!window.grecaptcha) {
            console.error('reCAPTCHA not loaded');
            return;
        }
        const action = 'SIGNUP';
        const token = await window.grecaptcha.enterprise.execute('6LcgR1ksAAAAAJZkReC9pRRpkOthcTLcBzkkVjF0', { action: action });

        try {
            const res = await fetch('/api/crm/v1/form', {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'X-CSRF-Token': Util.cookies.getItem('crumb'),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    formId: hubspotFormId,
                    fields: {
                        lead_source___category: 'Inbound',
                        ...data
                    },
                    recaptchaToken: token,
                    recaptchaAction: action
                })
            });

            if (res.status === 200) {
                reset();
                setMessage(T('Thank you for signing up to our newsletter.'));
            } else {
                const data = await res.json();
                throw new Error(data?.message);
            }
        } catch (err) {
            console.error(err);

            setErrorMessage(T('Sorry, something went wrong. Please try again later.'));
        }
    };

    const fieldMissing = Object.keys(errors).find((k) => errors[k].type === 'required');

    return (
        <div className='my-44 md:my-70 flex flex-wrap -mx-10 sm:-mx-5 md:-mx-10'>
            <div className='flex flex-col w-full sm:w-6/12 px-10 sm:px-5 md:px-10'>
                <p className='type-style-3'>{title}</p>
                <p className='type-style-5 mt-30 sm:mt-20 sm:mr-20'>{body}</p>
            </div>
            <form
                className='flex flex-wrap w-full sm:w-6/12 mt-40 sm:mt-0 sm:pl-[10px] md:pl-0'
                onSubmit={handleSubmit(onSubmit)}>
                <div className='w-full px-10 sm:px-5 md:px-10 mb-20 md:mb-25'>
                    <Label
                        htmlFor='email'
                        required={true}
                        type='email'>
                        {T('Email Address')}
                    </Label>
                    <Input
                        placeholder={T('jane@example.com')}
                        {...register('email', { required: true })} />
                </div>
                <div className='w-full md:w-1/2 px-10 sm:px-5 md:px-10 mb-20 md:mb-25'>
                    <Label
                        htmlFor='firstname'
                        required={false}>
                        {T('First Name')}
                    </Label>
                    <Input
                        placeholder={T('Jane')}
                        {...register('firstname', { required: false })} />
                </div>

                <div className='w-full md:w-1/2 px-10 sm:px-5 md:px-10 mb-20 md:mb-25'>
                    <Label
                        htmlFor='company'
                        required={false}>
                        {T('Company')}
                    </Label>
                    <Input
                        placeholder={T('Company')}
                        {...register('company', { required: false })} />
                </div>
                <div className='justify-between items-center w-full px-10 sm:px-5 md:px-10 sm:text-right'>
                    {fieldMissing &&
                        <Warning className='flex items-center type-style-10 mb-20 mr-20 sm:mb-0'>
                            {T('You are missing one or more required fields.')}
                        </Warning>
                    }

                    {(!fieldMissing && errorMessage) &&
                        <Warning className='flex items-center type-style-10 mb-20 mr-20 sm:mb-0'>
                            {errorMessage}
                        </Warning>
                    }

                    {(!fieldMissing && !errorMessage && !isDirty && message) &&
                        <div className='flex items-center type-style-10 mb-20 mr-20 sm:mb-0'>
                            {message}
                        </div>
                    }

                    <SubmitButton
                        className='button button--primary rounded-button text-white type-style-7 px-20 py-15'>
                        {T('Submit')}
                    </SubmitButton>
                </div>
            </form>
        </div>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

Newsletter.propTypes = {
    body: PropTypes.string,
    title: PropTypes.string,
};

export default Newsletter;
